import React, { useCallback, useMemo } from "react";
import { View } from "react-native";
import Svg, { Path } from "react-native-svg";
import barcodes, { type Barcode } from "jsbarcode/src/barcodes";
import { AHText, AHTextFonts } from "arkh";

type Props = {
  value: string;
  containerWidth: number;
  height: number;
  format: "EAN13" | "EAN8" | "CODE39" | "CODE128";
  lineColor?: string;
  onError?(error: any): void;
};

function drawRect(x: number, y: number, width: number, height: number) {
  return `M${x},${y}h${width}v${height}h-${width}z`;
}

function getEAN13RectHeight(index: number, barCodeHeight: number) {
  return index === 1 ||
    index === 2 ||
    index === 15 ||
    index === 16 ||
    index === 29 ||
    index === 30
    ? barCodeHeight
    : barCodeHeight - 15;
}

function getEAN8ReactHeight(index: number, barCodeHeight: number) {
  return index === 1 ||
    index === 2 ||
    index === 11 ||
    index === 12 ||
    index === 21 ||
    index === 22
    ? barCodeHeight
    : barCodeHeight - 15;
}

const barCodePadding = 15;

export function AHBarcode({
  value,
  containerWidth,
  height,
  lineColor = "#000000",
  format,
  onError,
}: Props) {
  const barCodeWidth = useMemo(
    () => containerWidth - barCodePadding * 2,
    [containerWidth]
  );
  const encode = useCallback(
    (text: string, Encoder: typeof Barcode) => {
      if (text.length === 0) {
        throw new Error("Barcode value must be a non-empty string");
      }
      const encoder = new Encoder(text, {
        width: barCodeWidth,
        format,
        height,
        lineColor,
        flat: true,
      });
      if (!encoder.valid()) {
        throw new Error("Invalid barcode for selected format.");
      }
      return encoder.encode();
    },
    [format, height, lineColor, barCodeWidth]
  );

  const encoder = useMemo(() => barcodes[format], [format]);

  const encoded = useMemo(
    () => encode(value, encoder),
    [encode, encoder, value]
  );

  const singleBarWidth = useMemo(() => {
    return barCodeWidth / encoded.data.length;
  }, [encoded.data.length, barCodeWidth]);

  const drawSvgBarCode = useCallback(
    (encoded: { data: string[] }) => {
      const rects = [];
      const { data: binary } = encoded;
      let barWidth = 0;
      let x = 0;
      const yFrom = 0;

      let drawnRects = 0;

      for (let b = 0; b < binary.length; b++) {
        x = b * singleBarWidth;
        if (binary[b] === "1") {
          barWidth++;
        } else if (barWidth > 0) {
          drawnRects++;
          rects[rects.length] = drawRect(
            x - singleBarWidth * barWidth,
            yFrom,
            singleBarWidth * barWidth,
            format === "EAN13"
              ? getEAN13RectHeight(drawnRects, height)
              : format === "EAN8"
                ? getEAN8ReactHeight(drawnRects, height)
                : height - 15
          );
          barWidth = 0;
        }
      }

      if (barWidth > 0) {
        rects[rects.length] = drawRect(
          x - singleBarWidth * (barWidth - 1),
          yFrom,
          singleBarWidth * barWidth,
          format === "EAN13"
            ? getEAN13RectHeight(drawnRects, height)
            : format === "EAN8"
              ? getEAN8ReactHeight(drawnRects, height)
              : height - 15
        );
        drawnRects++;
      }
      return rects;
    },
    [format, height, singleBarWidth]
  );

  const bars = useMemo(
    () => drawSvgBarCode(encoded),
    [encoded, drawSvgBarCode]
  );

  return (
    <View
      style={[
        {
          alignItems: "center",
          height,
          width: barCodeWidth,
          paddingHorizontal: "7%",
          alignSelf: "center",
        },
      ]}
    >
      <Svg height={height} width={barCodeWidth} fill={lineColor}>
        <Path d={bars.join(" ")} />
      </Svg>
      {format === "EAN13" ? (
        <AHText
          style={{
            bottom: -4,
            left: "-7%",
            position: "absolute",
            fontSize: 15,
          }}
          font={AHTextFonts.MONO}
        >
          {value.slice(0, 1)}
        </AHText>
      ) : null}
      <AHText
        style={{
          position: "absolute",
          bottom: -4,
          left: format !== "EAN8" ? "15%" : "19%",
          fontSize: 15,
        }}
        font={AHTextFonts.MONO}
      >
        {format === "CODE39" || format === "CODE128"
          ? value
          : value.slice(format === "EAN13" ? 1 : 0, format === "EAN13" ? 7 : 4)}
      </AHText>
      {format !== "CODE39" && format !== "CODE128" && (
        <AHText
          style={{
            position: "absolute",
            bottom: -4,
            right: format === "EAN13" ? "15%" : "19%",
            fontSize: 15,
          }}
          font={AHTextFonts.MONO}
        >
          {value.slice(format === "EAN13" ? 7 : 4)}
        </AHText>
      )}
    </View>
  );
}
